import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiStudioOrdersInit } from '@wix/da-papi-types';
import { DATE_FORMATS, formatDate } from '@wix/da-i18next/pkg/time/functions';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import DeviationLink from '@wix/da-shared-react/pkg/Duperbrowse/DeviationLink';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { changeOriginalStatus } from '../../../actions/studio/originals';
import { OriginalStatusType } from '../../../types/originals';
import OrdersList from '../OrdersList';
import { STUDIO_ORDERS_BY_DEVIATION_STREAM } from '../../../../constants';

import s from './OriginalOrdersCard.scss';

interface Props {
  order: PapiStudioOrdersInit['orders'][number];
}

const OriginalOrdersCard: React.FC<Props> = ({ order }) => {
  const { t, i18n } = useTranslation();
  const { deviation } = order;
  const [isOpen, setIsOpen] = useState(deviation.originals?.isOpen ?? false);
  const dispatch = useDispatch();

  const {
    items: results,
    hasMore,
    fetchNext,
  } = useStream(STUDIO_ORDERS_BY_DEVIATION_STREAM.getStreamIdForDeviation(deviation.deviationId));

  const onStatusToggle = useCallback(
    (newValue: boolean) => {
      dispatch(
        changeOriginalStatus({
          deviation,
          status: newValue ? OriginalStatusType.OPEN : OriginalStatusType.CLOSED,
        })
      );
      setIsOpen(newValue);
    },
    [dispatch, deviation]
  );

  if (!deviation) {
    return null;
  }

  return (
    <div className={s['root']}>
      <div className={s['header']}>
        <DeviationLink deviation={deviation}>
          <Dumbo deviation={deviation} width={150} height={75} />
        </DeviationLink>
        <div className={s['details']}>
          <DeviationLink className={s['title']} deviation={deviation}>
            {deviation.title}
          </DeviationLink>
          <div className={s['extra-info']}>
            {t('studio.orders.original.extraInfo', {
              publishedDate: formatDate(
                deviation.publishedTime,
                DATE_FORMATS.Date.Short,
                i18n.language
              ),
              basePrice: formatDollars(deviation.originals?.basePrice ?? 0),
            })}
          </div>
        </div>
        <label id={`${deviation.deviationId}-status-toggle-label`} className={s['status-toggle']}>
          {t('studio.orders.original.openToggleLabel')}
          <MainToggle
            variant="main"
            id={`${deviation.deviationId}-status-toggle`}
            size={'small'}
            value={isOpen}
            onChange={onStatusToggle}
            aria-labelledby={`${deviation.deviationId}-status-toggle-label`}
          />
        </label>
      </div>
      {results.length < 1 && (
        <div className={s['no-results']}>{t('studio.orders.original.emptyState')}</div>
      )}
      {results.length > 0 && (
        <OrdersList
          parentDeviation={deviation}
          results={results}
          hasMore={hasMore}
          fetchNext={fetchNext}
          showTitle={false}
          showStatus={true}
        />
      )}
    </div>
  );
};
OriginalOrdersCard.displayName = 'OriginalOrdersCard';

export default OriginalOrdersCard;
